<template>
  <div class="vuse-content-wrapper mt-4">

    <!-- ── Header row ──────────────────────────────────────────────────── -->
    <v-row>
      <v-col cols="12" md="4" class="mt-0">
        <h2 class="ml-3">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn fab icon small color="info" v-on="on" @click="loadData" :loading="loading" class="neu-glow mr-2">
                <v-icon>mdi-sync</v-icon>
              </v-btn>
            </template>
            <span>Aggiorna</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn fab icon small color="success" v-on="on" @click="exportCsv" class="neu-glow mr-2">
                <v-icon>mdi-microsoft-excel</v-icon>
              </v-btn>
            </template>
            <span>Esporta CSV</span>
          </v-tooltip>
          Stripe Fees
        </h2>
      </v-col>
      <v-spacer />
      <v-col cols="12" md="3" class="text-right pr-6">
        <v-btn color="primary" outlined @click="dateDialog = true">
          <v-icon left>mdi-calendar</v-icon>
          {{ rangeLabel }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- ── Summary cards ───────────────────────────────────────────────── -->
    <v-row class="mx-2 mt-2" v-if="totals">
      <v-col cols="6" sm="4" md="2">
        <v-card class="neu-glow text-center pa-3">
          <div class="caption text-uppercase grey--text">Earned</div>
          <div class="title green--text font-weight-bold">{{ centsToEur(totals.totalEarned) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="neu-glow text-center pa-3">
          <div class="caption text-uppercase grey--text">Pending</div>
          <div class="title amber--text darken-2 font-weight-bold">{{ centsToEur(totals.totalPending) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="neu-glow text-center pa-3">
          <div class="caption text-uppercase grey--text">Cancelled</div>
          <div class="title red--text lighten-2 font-weight-bold">{{ centsToEur(totals.totalCancelled) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="neu-glow text-center pa-3">
          <div class="caption text-uppercase grey--text">Refunded</div>
          <div class="title orange--text font-weight-bold">{{ centsToEur(totals.totalRefunded) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="neu-glow text-center pa-3">
          <div class="caption text-uppercase grey--text">Parziale</div>
          <div class="title orange--text lighten-2 font-weight-bold">{{ centsToEur(totals.totalPartialRefunded) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card class="neu-glow text-center pa-3">
          <div class="caption text-uppercase grey--text">Transazioni</div>
          <div class="title font-weight-bold">{{ totals.totalRows || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ── Data table ──────────────────────────────────────────────────── -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card class="neu-glow pb-4">
          <v-card-title>
            <v-spacer />
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Cerca"
              single-line
              hide-details
              style="max-width: 280px"
            />
          </v-card-title>

          <v-data-table
            dense
            fixed-header
            :headers="headers"
            :items="fees"
            :search="search"
            :loading="loading"
            loading-text="Caricamento..."
            no-data-text="Nessun dato disponibile"
            :items-per-page="Math.max(10, Math.floor((wIH - 420) / 40))"
            :height="wIH - 420"
            item-key="feeId"
            multi-sort
            class="elevation-1 mx-4"
          >
            <!-- Status chip -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small :color="statusColor(item.status)" dark>
                {{ item.status }}
              </v-chip>
            </template>

            <!-- Fee amount (cents → EUR) -->
            <template v-slot:[`item.feeAmount`]="{ item }">
              <span class="font-weight-medium">{{ centsToEur(item.feeAmount) }}</span>
            </template>

            <!-- Payment amount -->
            <template v-slot:[`item.paymentAmountEur`]="{ item }">
              {{ item.paymentAmountEur != null ? '€ ' + parseFloat(item.paymentAmountEur).toFixed(2) : '' }}
            </template>

            <!-- Capture method chip -->
            <template v-slot:[`item.captureMethod`]="{ item }">
              <v-chip x-small :color="item.captureMethod === 'manual' ? 'blue' : 'grey'" dark>
                {{ item.captureMethod || '-' }}
              </v-chip>
            </template>

            <!-- Booking date -->
            <template v-slot:[`item.bookingDate`]="{ item }">
              {{ item.bookingDate || '-' }}
            </template>

            <!-- Created at -->
            <template v-slot:[`item.createdAt`]="{ item }">
              {{ item.createdAt ? new Date(item.createdAt).toLocaleString('it-IT') : '' }}
            </template>

            <!-- PaymentIntent (truncated) -->
            <template v-slot:[`item.paymentIntentId`]="{ item }">
              <span class="caption grey--text" :title="item.paymentIntentId">
                {{ item.paymentIntentId ? item.paymentIntentId.slice(0, 20) + '…' : '' }}
              </span>
            </template>

          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- ── Date range dialog ───────────────────────────────────────────── -->
    <v-dialog v-model="dateDialog" max-width="360">
      <v-card>
        <v-card-title class="text-h6">Seleziona periodo</v-card-title>
        <v-card-text>
          <v-date-picker
            v-model="dateRange"
            range
            full-width
            locale="it"
            no-title
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dateDialog = false">Annulla</v-btn>
          <v-btn color="primary" dark @click="applyDateRange">Applica</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'BossStripe',

  computed: {
    ...mapGetters(['user', 'settings']),

    rangeLabel() {
      if (!this.dateRange || this.dateRange.length < 2) return 'Periodo'
      const [a, b] = [...this.dateRange].sort()
      return `${a}  →  ${b}`
    },
  },

  data() {
    return {
      fees: [],
      totals: null,
      loading: false,
      search: '',
      dateDialog: false,
      dateRange: [
        new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
        new Date().toISOString().slice(0, 10),
      ],
      wIH: window.innerHeight,

      headers: [
        { text: 'Ristorante',   value: 'restaurantName',   sortable: true  },
        { text: 'Stato',        value: 'status',           sortable: true  },
        { text: 'Fee (€)',      value: 'feeAmount',        sortable: true  },
        { text: 'Pagamento',    value: 'paymentAmountEur', sortable: true  },
        { text: 'Cattura',      value: 'captureMethod',    sortable: true  },
        { text: 'Prenotazione', value: 'bookingDate',      sortable: true  },
        { text: 'Cliente',      value: 'customerName',     sortable: true  },
        { text: 'PI',           value: 'paymentIntentId',  sortable: false },
        { text: 'Creato',       value: 'createdAt',        sortable: true  },
      ],
    }
  },

  mounted() {
    this.loadData()
  },

  methods: {
    async loadData() {
      this.loading = true
      try {
        const [start, end] = [...this.dateRange].sort()
        const res = await this.$http.post('/boss/stripe.php', {
          action: 'getFees',
          payload: { dateStart: start, dateStop: end },
        })
        if (res.data && res.data.errorid === '0') {
          this.fees   = res.data.payloads?.fees   || []
          this.totals = res.data.payloads?.totals || null
        }
      } catch (e) {
        console.error('StripeFees loadData error:', e)
      } finally {
        this.loading = false
      }
    },

    applyDateRange() {
      this.dateDialog = false
      this.loadData()
    },

    statusColor(status) {
      const map = {
        earned:             'green',
        pending:            'amber darken-2',
        cancelled:          'red lighten-2',
        refunded:           'orange',
        partially_refunded: 'orange lighten-2',
      }
      return map[status] || 'grey'
    },

    centsToEur(cents) {
      if (cents == null) return '€ 0.00'
      return '€ ' + (cents / 100).toFixed(2)
    },

    exportCsv() {
      if (!this.fees.length) return
      const cols   = this.headers.map(h => h.value)
      const labels = this.headers.map(h => h.text)
      const rows   = [labels.join(';')]
      this.fees.forEach(f => {
        rows.push(cols.map(c => {
          const v = f[c]
          if (c === 'feeAmount') return (v / 100).toFixed(2)
          return v != null ? String(v).replace(/;/g, ',') : ''
        }).join(';'))
      })
      const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `stripe_fees_${this.dateRange[0]}_${this.dateRange[1]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>
